// Q35 — save/resume a project via a shareable link.
// snapshotState() picks exactly the persisted wizard-store fields; loadProject()
// hydrates them back. Room photos live in IndexedDB (device-local) and do NOT
// travel with the link — the resumed project keeps the config, not the photos.

import { useWizardStore } from '../store/wizardStore';

const PERSIST_KEYS = [
  'sessionId', 'accessToken', 'wizardType', 'installTier', 'divisions',
  'selections', 'devices', 'floors', 'activeFloorId', 'activeDivisionId',
  'globalColor', 'roomPlacements', 'roomDecor', 'roomSurveyNote',
] as const;

export interface SaveCustomer {
  name: string;
  email: string;
  phone?: string;
}

export function snapshotState(): Record<string, unknown> {
  const s = useWizardStore.getState() as unknown as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const k of PERSIST_KEYS) out[k] = s[k];
  return out;
}

// Short human summary for the lead (e.g. "T2 · 3 divisões · 5 componentes").
export function projectSummary(): string {
  const s = useWizardStore.getState();
  const divs = s.divisions.filter((d) => d.optedIn).length;
  const comps = Object.values(s.selections).reduce((n, arr) => n + (arr?.length ?? 0), 0);
  return [
    s.wizardType ?? '?',
    `${divs} ${divs === 1 ? 'divisão' : 'divisões'}`,
    `${comps} ${comps === 1 ? 'componente' : 'componentes'}`,
  ].join(' · ');
}

export async function saveProject(
  customer: SaveCustomer,
): Promise<{ id: string; token: string }> {
  const res = await fetch('/api/projects', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      state: snapshotState(),
      customer,
      summary: projectSummary(),
      consent: true,
    }),
  });
  const json = (await res.json()) as { success: boolean; data?: { id: string; token: string }; message?: string };
  if (!json.success || !json.data) throw new Error(json.message ?? 'Não foi possível guardar.');
  return json.data;
}

export function resumeLink(id: string, token: string): string {
  return `${window.location.origin}/?p=${id}&t=${encodeURIComponent(token)}`;
}

// Returns the wizardType to navigate to, or null on failure.
export async function loadProject(id: string, token: string): Promise<string | null> {
  try {
    const res = await fetch(`/api/projects/${encodeURIComponent(id)}?token=${encodeURIComponent(token)}`);
    const json = (await res.json()) as { success: boolean; data?: { state: Record<string, unknown> } };
    if (!json.success || !json.data?.state) return null;
    const state = json.data.state;
    useWizardStore.setState(state as never);
    return (state.wizardType as string | null) ?? null;
  } catch {
    return null;
  }
}
